import React, { useState } from 'react';
import { Alert, Button, Container, Spinner } from 'react-bootstrap';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import { useMembership } from '@/shared/contexts/MembershipContext';
import { useEcosystemApi } from '@/shared/services/ecosystemApi';

/**
 * JoinEcosystemBanner
 *
 * A dismissible prompt inviting a logged-in non-member to join the ecosystem
 * they are currently browsing. Rendered once, near the top of every page
 * (just under the NavBar in App.js).
 *
 * Visibility rules: the banner shows ONLY when ALL hold:
 *   - a tenant ecosystem is resolved (not the platform apex), and
 *   - a Firebase user is signed in and email-verified (signed-out visitors
 *     sign up, which auto-joins; they are never shown this), and
 *   - that user is NOT already an active member of the ecosystem.
 *
 * Membership is read from MembershipContext, which keys off the server's
 * explicit `currentEcosystemMembership` signal, never off whether the
 * mentor/affiliation fields happen to be empty.
 *
 * On a successful join the membership state is refreshed via
 * MembershipContext.refresh(), so this banner unmounts itself and any
 * member-gated UI elsewhere becomes available without a manual page reload.
 *
 * Dismissal is per-mount only (local state), intentionally lightweight; the
 * banner reappears on the next visit until the user joins.
 */
export default function JoinEcosystemBanner() {
    const { user: firebaseUser } = useAuth();
    const { ecosystem, isPlatform } = useEcosystem();
    const { isMember, loading: membershipLoading, refresh } = useMembership();
    const ecosystemApi = useEcosystemApi();

    const [dismissed, setDismissed] = useState(false);
    const [joining, setJoining] = useState(false);
    const [error, setError] = useState(null);

    const isLoggedIn = Boolean(firebaseUser && firebaseUser.emailVerified);

    // Do not render in platform mode, for signed-out visitors, for existing
    // members, while the membership signal is still resolving, or once the
    // user has dismissed it for this session.
    if (
        isPlatform ||
        !ecosystem ||
        !isLoggedIn ||
        membershipLoading ||
        isMember ||
        dismissed
    ) {
        return null;
    }

    const ecosystemName = ecosystem.name || ecosystem.slug;

    const handleJoin = async () => {
        setJoining(true);
        setError(null);
        try {
            await ecosystemApi.joinEcosystem(ecosystem.slug);
            // Refresh the membership signal: once active, isMember flips
            // true and this banner stops rendering on the next pass.
            await refresh();
        } catch {
            setError('We could not complete your request. Please try again.');
        } finally {
            // Round-4 review finding 10: always re-enable the button when
            // the request settles. Previously this only ran in the catch
            // branch, so if the join succeeded but `isMember` didn't flip
            // true (membership reactivated to `pending` status; DocumentDB
            // secondary returning a stale read; refresh throws separately)
            // the banner stayed mounted with the button permanently
            // disabled showing the "Joining…" spinner. With the banner
            // still mounted the user can retry: if isMember DOES flip, the
            // banner unmounts and the state reset is irrelevant.
            setJoining(false);
        }
    };

    return (
        <Container className="mt-3">
            <Alert
                variant="info"
                dismissible
                onClose={() => setDismissed(true)}
                className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-0"
            >
                <div>
                    <Alert.Heading className="h6 mb-1">
                        Join {ecosystemName}
                    </Alert.Heading>
                    <span className="small mb-0">
                        Become a member to connect with organizations and unlock
                        everything {ecosystemName} has to offer.
                    </span>
                    {error && (
                        <div className="text-danger small mt-1">{error}</div>
                    )}
                </div>
                <Button
                    variant="primary"
                    className="rounded-pill flex-shrink-0"
                    onClick={handleJoin}
                    disabled={joining}
                >
                    {joining ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                className="me-2"
                            />
                            Joining…
                        </>
                    ) : (
                        `Join ${ecosystemName}`
                    )}
                </Button>
            </Alert>
        </Container>
    );
}
